import React, {createRef, CSSProperties, ReactNode, RefObject} from "react";
import AbstractContext from "../AbstractContext";
import DomHelper from "../helper/DomHelper";
import LengthUnit from "../units/LengthUnit";
import Translate from "../translation/Translate";
import {I18nGlobals} from "../translation/i18nInterface";
import MenuButton from "../menu/MenuButton";
import FunctionHelper from "../helper/FunctionHelper";
import PortalPureComponent from "./PortalPureComponent";

/**
 * globally used properties for {@link HelpOverlay} components
 */
export interface HelpOverlayGlobals {
	showHelpScreen: boolean;
}

/**
 * The properties for a {@link HelpOverlay} component
 */
interface HelpOverlayProperies extends HelpOverlayGlobals, I18nGlobals {
	context: AbstractContext<any, any>;
}

/**
 * The status of a {@link HelpOverlay} component
 */
interface HelpOverlayStatus {
	items: Array<HelpItem>;
	currentIndex: number;
}

/**
 * An item on the page to be shown in the {@link HelpOverlay}. description is filled by the translation key of the
 * **data-help** attribute and the description position can be set by the **data-position** attribute with the preset
 * {@link HelpPosition}s.
 */
interface HelpItem {
	position: DOMRect, // in Pixel
	description: string, // translation key
	direction: HelpPosition
}

/**
 * Relative position of the describing text of a {@link HelpItem}
 */
enum HelpPosition {
	LEFT = "left",
	RIGHT = "right",
	TOP = "top",
	BOTTOM = "bottom"
}

/**
 * This component is an overlay showing one by one each item on the page with **data-help** attribute set. These are
 * represented by {@link HelpItem}s.
 */
export default class HelpOverlay extends PortalPureComponent<HelpOverlayProperies, HelpOverlayStatus> {
	private resizeObserver: ResizeObserver;
	private overlay: RefObject<HTMLDivElement>;

	/**
	 * Constructs a {@link HelpOverlay} instance
	 */
	constructor(props: Readonly<HelpOverlayProperies>) {
		super(props);

		this.state = {
			items: [],
			currentIndex: 0
		}

		this.overlay = createRef<HTMLDivElement>();
		this.resizeObserver = new ResizeObserver(() => {
			this.setState({
				items: this.generateItems()
			});
		});
		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleClick = this.handleClick.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('keydown', this.handleKeydown);

		if (this.overlay.current !== null) {
			this.resizeObserver.observe(this.overlay.current);
		}
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeydown);

		this.resizeObserver.disconnect();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<HelpOverlayProperies>, prevState: Readonly<any>, snapshot?: any
	): Promise<void> {
		if (prevProps.showHelpScreen !== this.props.showHelpScreen && this.props.showHelpScreen) {
			this.setState({
				currentIndex: 0,
				items: this.generateItems()
			});
		}
	}

	/**
	 * Handles keyboard key down events
	 */
	private async handleKeydown(e: KeyboardEvent): Promise<void> {
		let currentIndex: number = this.state.currentIndex;

		switch (e.key.toLowerCase()) {
			case "escape": // end the overlay
				this.props.context.getPage().setState({
					showHelpScreen: false
				});
				break;
			case "arrowleft": // move to the previous HelpItem
				currentIndex--;
				this.selectItem(currentIndex);
				break;
			case "arrowright": // move to the next HelpItem
				currentIndex++;
				this.selectItem(currentIndex);
				break;
		}
	}

	/**
	 * Handles click events
	 */
	private handleClick(e: React.MouseEvent): void {
		e.preventDefault();

		let currentIndex: number = this.state.currentIndex;
		if (e.button === 0) {
			// left click move to the next HelpItem
			currentIndex++;
		} else {
			// right click move to the previous HelpItem
			currentIndex--;
		}

		this.selectItem(currentIndex);
	}

	/**
	 * Generates {@link HelpItem}s by searching for all elements with **data-help** attributes, starting at document
	 * root.
	 */
	private generateItems(): Array<HelpItem> {
		let elements: Array<HTMLElement> = DomHelper.findAll(
			document.getElementById("root")!, "[data-help]"
		);
		let items: Array<HelpItem> = [];

		for (let element of elements) {
			let computedStyle: CSSStyleDeclaration = window.getComputedStyle(element);
			if (element.hidden || computedStyle.display === "none" || computedStyle.visibility === "hidden" ||
				element.offsetParent === null) {
				continue;
			}

			let description: string = "";
			if (FunctionHelper.exists(element.dataset.help)) {
				description = element.dataset.help!
			}

			let direction: HelpPosition = HelpPosition.RIGHT;
			if (FunctionHelper.exists(element.dataset.position)) {
				direction = element.dataset.position as HelpPosition
			}

			items.push({
				description: description,
				direction: direction,
				position: element.getBoundingClientRect()
			} as HelpItem);
		}

		return items;
	}

	/**
	 * Renders the currently active {@link HelpItem} as {@link ReactNode}, selected by the
	 * {@link HelpOverlay.state.currentIndex}.
	 */
	private renderCurrentItem(): ReactNode {
		let item: HelpItem | undefined = this.state.items[this.state.currentIndex];
		if (!FunctionHelper.exists(item)) {
			return null;
		}

		let nodeClassList: Array<string> = ["help-node", item.direction];
		let nodeStyle: CSSProperties;

		switch (item.direction) {
			case HelpPosition.TOP:
				nodeStyle = {
					width: item.position.width + LengthUnit.PIXEL,
					left: item.position.left + LengthUnit.PIXEL,
					top: item.position.top + LengthUnit.PIXEL
				};
				break;
			case HelpPosition.BOTTOM:
				nodeStyle = {
					width: item.position.width + LengthUnit.PIXEL,
					left: item.position.left + LengthUnit.PIXEL,
					top: item.position.bottom + LengthUnit.PIXEL
				};
				break;
			case HelpPosition.LEFT:
				nodeStyle = {
					height: item.position.height + LengthUnit.PIXEL,
					left: item.position.left + LengthUnit.PIXEL,
					top: item.position.top + LengthUnit.PIXEL
				};
				break;
			case HelpPosition.RIGHT:
				nodeStyle = {
					height: item.position.height + LengthUnit.PIXEL,
					left: item.position.right + LengthUnit.PIXEL,
					top: item.position.top + LengthUnit.PIXEL
				};
				break;
		}

		return (
			<>
				<div className="item-marker" style={{
					width: item.position.width + LengthUnit.PIXEL,
					height: item.position.height + LengthUnit.PIXEL,
					top: item.position.top + LengthUnit.PIXEL,
					left: item.position.left + LengthUnit.PIXEL
				}}/>
				<div className={nodeClassList.join(" ")} style={nodeStyle}>
					<div className="help-description">
						<Translate {...this.props} data={item.description} markdown={true} namespace="help"/>
					</div>
				</div>
			</>
		)
	}

	/**
	 * Selects the currently active {@link HelpItem}, setting {@link HelpOverlay.state.currentIndex}
	 */
	private selectItem(index: number): void {
		let item: HelpItem | undefined = this.state.items[index];
		if (!FunctionHelper.exists(item)) {
			// if item doesn't exist close the overlay
			this.props.context.getPage().setState({
				showHelpScreen: false
			});
			return;
		}

		this.setState({
			currentIndex: index
		});
	}

	/**
	 * Renders a toolbar for navigation at the bottom of the {@link HelpOverlay} as {@link ReactNode}
	 */
	private renderToolbar(): ReactNode {
		let currentIndex: number = this.state.currentIndex;
		let lastIndex: number = this.state.items.length - 1;

		return (
			<div className="help-panel" onClick={(e) => {
				e.stopPropagation()
			}}>
				<MenuButton {...this.props} disabled={currentIndex <= 0} icon="icon icon-first"
							tooltip={Translate.value("global.overlay.first", {ns: "help"})}
							className="help-overlay-first-button"
							onClick={(e) => {
								// select first item
								e.stopPropagation();
								this.selectItem(0);
							}}/>
				<MenuButton {...this.props} disabled={currentIndex <= 0} icon="icon icon-previous"
							tooltip={Translate.value("global.overlay.previous", {ns: "help"})}
							className="help-overlay-previous-button"
							onClick={(e) => {
								// select previous item
								e.stopPropagation();
								currentIndex--;
								this.selectItem(currentIndex);
							}}/>
				<p className="index">{currentIndex + 1} / {lastIndex + 1}</p>
				<MenuButton {...this.props} disabled={currentIndex >= lastIndex} icon="icon icon-next"
							tooltip={Translate.value("global.overlay.next", {ns: "help"})}
							className="help-overlay-next-button"
							onClick={(e) => {
								// select next item
								e.stopPropagation();
								currentIndex++;
								this.selectItem(currentIndex);
							}}/>
				<MenuButton {...this.props} disabled={currentIndex >= lastIndex} icon="icon icon-last"
							tooltip={Translate.value("global.overlay.last", {ns: "help"})}
							className="help-overlay-last-button"
							onClick={(e) => {
								// select last item
								e.stopPropagation();
								this.selectItem(lastIndex);
							}}/>
				<MenuButton {...this.props} icon="icon icon-close" className="help-overlay-close-button"
							tooltip={Translate.value("global.overlay.close", {ns: "help"})}
							onClick={(e) => {
								// close the overlay
								e.stopPropagation();
								this.props.context.getPage().setState({
									showHelpScreen: false
								});
							}}/>
			</div>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<div className="help-overlay" style={this.props.showHelpScreen ? {} : {display: "none"}}
				 onClick={this.handleClick} onContextMenu={this.handleClick} ref={this.overlay}>
				{this.renderCurrentItem()}
				{this.renderToolbar()}
			</div>
		);
	}
}